import { ArrowDownTrayIcon, CheckIcon, TrashIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { memo, useCallback, useMemo } from 'react';
import { CircleSpinner } from '../../../../components/Button';
import { useDownloadExport } from '../../../../hooks/useDownloadExport';
import { useFormatDate } from '../../../../hooks/useFormatDate';
import { useProject } from '../../../../hooks/potree/useProject';
import { useSeenExports } from '../../../../hooks/useSeenExports';
import { T, useT } from '../../../../translation/src';
import { Export, GeneralStatus, useProjectDeleteExportMutation } from '../../../../types/graphqlTypes';
import { HeaderDropdownButton } from '../HeaderDropdownButton';

// Only point cloud exports flow through the server (blob storage + Pusher).
// IFC and DXF are generated client-side and downloaded immediately — they never show up here.
const REMOTE_EXPORT_TYPES = new Set(['exportPointcloud']);

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let size = bytes / 1024;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }
  return `${size.toFixed(1)} ${units[unitIndex]}`;
};

const getExportDisplayName = (paramInfo: Export['paramInfo']): string => {
  if (paramInfo.__typename === 'PointCloudExportParamInfo') return paramInfo.fileType || 'Point Cloud';
  return 'Export';
};

const ExportsDownloadButton_: React.FC = () => {
  const projectQuery = useProject();
  const { downloadExport } = useDownloadExport();
  const { formatDateAndTime } = useFormatDate();
  const { markSeen } = useSeenExports();
  const [deleteExportMutation] = useProjectDeleteExportMutation();

  const projectId = projectQuery.data?.projectById?.id;

  const relevantExports = useMemo(() => {
    const all = projectQuery.data?.projectById?.exports ?? [];
    return all
      .filter((exp): exp is Export & { exportId: string } => Boolean(exp.exportId) && REMOTE_EXPORT_TYPES.has(exp.type) && (exp.status === GeneralStatus.Progress || exp.status === GeneralStatus.Success))
      .slice()
      .sort((a, b) => {
        const aDate = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bDate = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return bDate - aDate;
      });
  }, [projectQuery.data?.projectById?.exports]);

  const hasInProgress = relevantExports.some((exp) => exp.status === GeneralStatus.Progress);
  const unseenIds = useMemo(() => relevantExports.filter((exp) => exp.status === GeneralStatus.Success && !exp.seen).map((exp) => exp.exportId), [relevantExports]);
  const hasUnseenReady = unseenIds.length > 0;
  const markAllSeenTooltip = useT('mark all as seen', { swc: true });

  const markAllSeen = useCallback(() => {
    if (!projectId || unseenIds.length === 0) return;
    markSeen(projectId, unseenIds);
  }, [projectId, unseenIds, markSeen]);

  if (relevantExports.length === 0 || !projectId) return null;

  return (
    <HeaderDropdownButton
      icon={ArrowDownTrayIcon}
      title={<T _str="Downloads" swc />}
      inProgress={hasInProgress}
      showDot={hasUnseenReady}
      headerAction={
        hasUnseenReady ? (
          <button
            type="button"
            onClick={markAllSeen}
            title={markAllSeenTooltip}
            aria-label={markAllSeenTooltip}
            className="flex items-center justify-center p-1 rounded text-gray-500 hover:text-green-600 dark:text-gray-400 dark:hover:text-green-400 cursor-pointer transition-colors"
          >
            <CheckIcon className="w-4 h-4" />
          </button>
        ) : undefined
      }
    >
      {relevantExports.map((exp) => {
        const isProgress = exp.status === GeneralStatus.Progress;
        const isSuccess = exp.status === GeneralStatus.Success;
        const unseen = isSuccess && !exp.seen;
        const displayName = getExportDisplayName(exp.paramInfo);
        const typeLabel = 'Point Cloud';
        const dateText = exp.createdAt ? formatDateAndTime(new Date(exp.createdAt)) : '';
        const sizeText = typeof exp.fileSize === 'number' ? formatBytes(exp.fileSize) : null;
        return (
          <div
            key={exp.exportId}
            role="button"
            tabIndex={0}
            onClick={() => markSeen(projectId, exp.exportId)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') markSeen(projectId, exp.exportId);
            }}
            className={classNames(
              'flex items-center gap-2.5 px-3 py-2 border-b border-gray-100 dark:border-gray-700 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer',
              // Unseen: a green line on the left edge, as in the notifications list.
              unseen && 'shadow-[inset_3px_0_0_var(--color-green-500)]',
            )}
          >
            <div className="min-w-0 shrink">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium truncate">{displayName}</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                <span>{typeLabel}</span>
                {dateText && <span>·</span>}
                {dateText && <span className="truncate">{dateText}</span>}
                {sizeText && <span>·</span>}
                {sizeText && <span>{sizeText}</span>}
              </div>
            </div>
            <div className="flex-1 min-w-4" />
            <div className="shrink-0 flex items-center gap-2">
              {isProgress && (
                <div className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                  <CircleSpinner className="w-4 h-4" />
                  <T _str="In progress" swc />
                </div>
              )}
              {isSuccess && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    markSeen(projectId, exp.exportId);
                    downloadExport({ projectId, exportId: exp.exportId }).catch((err) => console.error('Error downloading export:', err));
                  }}
                  className="flex items-center justify-center p-1.5 rounded bg-french-violet-600 text-white cursor-pointer transition-all hover:brightness-110 active:brightness-95"
                  title="download"
                >
                  <ArrowDownTrayIcon className="w-4 h-4" />
                </button>
              )}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  deleteExportMutation({ variables: { projectId, exportId: exp.exportId } }).catch((err) => console.error('Error deleting export:', err));
                }}
                className="flex items-center justify-center p-1 rounded text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400 cursor-pointer transition-colors"
                title="delete"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        );
      })}
    </HeaderDropdownButton>
  );
};

export const ExportsDownloadButton = memo(ExportsDownloadButton_);
